import { ACTIVITY_AUTOMATION_SOURCE_UNKNOWN, ACTIVITY_INPUT_SOURCE } from 'utils/activityTracking/constants';
import { AUTOMATION_SOURCE } from 'utils/constants';

/**
 * Maps AIQ's own record of where a field value came from (`form.fieldValueSources[fieldName]`) onto
 * the activity event stream's `input_source`, plus the stable automation source id that rides
 * alongside it (BEAST-13036).
 *
 * AIQ writes that record in two shapes: `markFieldUserEdited` / `setFieldValueAndMarkEdited` store
 * the literal `'user'`, and every automation path stores one of the `AUTOMATION_SOURCE` values.
 *
 * THE TRAP: `AUTOMATION_SOURCE.MANUAL_ANNOUNCEMENT` is the string `'Manual'`. A mapping written as
 * `source === 'Manual' ? 'manual' : 'automation'` reads correctly and is wrong - a value AIQ copied
 * out of a manual announcement record is automation, not agent entry. `manual` is true ONLY for the
 * user-edit marker `'user'`; all `AUTOMATION_SOURCE` members map to `automation`, the one spelled
 * `'Manual'` included. The unit test feeds this every member of the constant and asserts that.
 */

/** The literal `markFieldUserEdited` and `setFieldValueAndMarkEdited` write for agent entry. */
export const USER_EDIT_SOURCE = 'user';

/**
 * AIQ's `AUTOMATION_SOURCE` values translated to the wire vocabulary for `automation_source`.
 *
 * THE RAW VALUES CANNOT BE SENT. The backend validates this attribute against
 * `^[a-z][a-z0-9_]*$` (`ActivityEventEnvelopeValidator.AUTOMATION_SOURCE_VALUE`, 64 chars max), and
 * eight of the ten raw values are capitalized display abbreviations - `Auth`, `Inv`, `Equip`,
 * `Bison`, `Manual`, `App`, `Trx`, `Auto`. Sending those puts every automation
 * `field.value.applied` event into `rejected[]` while the request still answers 202. Their own
 * validator test pins this: `ocr_pipeline` is accepted, `OCR-Pipeline` is rejected.
 *
 * The rule is uniform - the CONSTANT NAME, lowercased - so there is one convention in the column
 * rather than a mix of derived and raw tokens. That also decouples the fact store from AIQ's
 * display abbreviations, which can change for UI reasons without anyone thinking about telemetry.
 *
 * Two worth calling out: `AI_OCR` is the raw string `'alisa'` and becomes `ai_ocr`, and
 * `MANUAL_ANNOUNCEMENT` is the raw string `'Manual'` and becomes `manual_announcement` - which
 * keeps the trap legible downstream, because it reads as the automation source it is.
 */
export const ACTIVITY_AUTOMATION_SOURCE_BY_AIQ_SOURCE = Object.freeze({
    [AUTOMATION_SOURCE.AUTH_LIST]: 'auth_list',
    [AUTOMATION_SOURCE.INVENTORY_ITEM]: 'inventory_item',
    [AUTOMATION_SOURCE.EQUIPMENT_ITEM]: 'equipment_item',
    [AUTOMATION_SOURCE.BISON_ANNOUNCEMENT]: 'bison_announcement',
    [AUTOMATION_SOURCE.MANUAL_ANNOUNCEMENT]: 'manual_announcement',
    [AUTOMATION_SOURCE.APPOINTMENT]: 'appointment',
    [AUTOMATION_SOURCE.HISTORICAL_TRANSACTION]: 'historical_transaction',
    [AUTOMATION_SOURCE.AI_OCR]: 'ai_ocr',
    [AUTOMATION_SOURCE.SHEET_ANNOUNCEMENT]: 'sheet_announcement',
    [AUTOMATION_SOURCE.CONDITIONALS]: 'conditionals',
    [AUTOMATION_SOURCE.AUTO]: 'auto',
    [AUTOMATION_SOURCE.CONDOR]: 'condor',
    [AUTOMATION_SOURCE.PROTOCOL]: 'protocol',
});

/**
 * @param {string|undefined|null} fieldValueSource the recorded `form.fieldValueSources[fieldName]`
 * @returns {{ inputSource: string, automationSource: string|null }} `automationSource` is `null` for
 *   manual entry, where the attribute is omitted entirely, and never `undefined`.
 */
export function resolveInputSource(fieldValueSource) {
    if (fieldValueSource === USER_EDIT_SOURCE) {
        // Architect decision of 2026-09-04: the source id is omitted for manual entry.
        return { inputSource: ACTIVITY_INPUT_SOURCE.MANUAL, automationSource: null };
    }

    // Everything else is automation, including a value with no recorded source at all - the bison
    // and conditionals autofill handlers write through plain `setFieldValue` and record none, and an
    // agent's keyboard is the only thing that ever records `'user'`.
    //
    // A source outside the known set degrades to `unknown` rather than putting an unvetted token on
    // the wire: a future `AUTOMATION_SOURCE` member would otherwise fail the backend's value
    // pattern and reject the event, silently, behind a 202.
    return {
        inputSource: ACTIVITY_INPUT_SOURCE.AUTOMATION,
        automationSource: ACTIVITY_AUTOMATION_SOURCE_BY_AIQ_SOURCE[fieldValueSource] ?? ACTIVITY_AUTOMATION_SOURCE_UNKNOWN,
    };
}
